import React, { useRef, useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface SharePointUrlGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GuideTab {
  key: string;
  label: string;
  title: string;
  example: string;
  subSteps: string[];
}

const tabs: GuideTab[] = [
  {
    key: 'list',
    label: 'SharePoint List',
    title: 'Find the URL of a SharePoint List',
    example: 'https://yourcompany.sharepoint.com/sites/YourSiteName/Lists/YourListName',
    subSteps: [
      'Navigate to the SharePoint site that contains the List.',
      'Click "Site contents" in the left sidebar navigation.',
      'Select the List you want to connect.',
      'When the List opens, copy the full URL from the browser\'s address bar.',
      'Paste the URL into the connection field, even if it appears longer than the example below.',
    ],
  },
  {
    key: 'doc_library',
    label: 'Document Library',
    title: 'Find the URL of a Document Library',
    example: 'https://yourcompany.sharepoint.com/sites/YourSiteName/Shared%20Documents',
    subSteps: [
      'Navigate to the SharePoint site that contains the Document Library.',
      'Click "Site contents" in the left sidebar navigation.',
      'Select the Document Library you want to connect.',
      'When the Document Library opens, copy the full URL from the browser\'s address bar.',
      'Paste the URL into the connection field, even if it appears longer than the example below.',
    ],
  },
  {
    key: 'doc_library_folder',
    label: 'Library Folder',
    title: 'Find the URL of a Document Library Folder',
    example: 'https://yourcompany.sharepoint.com/sites/YourSiteName/Shared%20Documents/FolderName',
    subSteps: [
      'Navigate to the SharePoint site that contains the Document Library.',
      'Click "Site contents" in the left sidebar navigation.',
      'Select the Document Library folder you want to connect.',
      'When the folder opens, copy the full URL from the browser\'s address bar.',
      'Paste the URL into the connection field, even if it appears longer than the example below.',
    ],
  },
];

const SharePointUrlGuideModal: React.FC<SharePointUrlGuideModalProps> = ({
  isOpen,
  onClose,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const currentTab = tabs[activeIndex];

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div
        ref={modalRef}
        className="relative bg-white rounded-lg shadow-lg w-full max-w-[52rem] mx-4 flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-4 pb-2">
          <h2 className="text-lg font-semibold text-gray-800">Where to Find Your SharePoint URL</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-black focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex">
            {tabs.map((tab, index) => {
              const isActive = activeIndex === index;
              return (
                <button
                  key={tab.key}
                  className={`px-4 py-2 border-b-2 font-medium transition-colors text-sm ${
                    isActive
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveIndex(index)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <h3 className="text-base font-semibold mb-4">{currentTab.title}</h3>

          {/* Numbered sub-steps */}
          <ol className="space-y-3 mb-6">
            {currentTab.subSteps.map((subStep, idx) => (
              <li key={idx} className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-semibold">
                  {idx + 1}
                </span>
                <p className="text-sm text-gray-700 leading-relaxed">{subStep}</p>
              </li>
            ))}
          </ol>

          {/* Example URL */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-xs text-gray-500 mb-1 font-medium">Example URL</p>
            <code className="text-sm text-gray-800 break-all">{currentTab.example}</code>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-black text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePointUrlGuideModal;
